import { useState, useEffect, useMemo } from 'react'
import { useParams, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '@/contexts/auth-context'
import { useAuthActions } from '@convex-dev/auth/react'
import { isElectron } from '@/lib/electron'
import { Menu } from 'lucide-react'

import { Navbar } from '@/components/homepage/sections/Navbar'
import { SectionHeader } from '@/components/homepage/ui/SectionHeader'
import { HomepageFooter } from '@/components/homepage/sections/HomepageFooter'
import { DocsSidebar } from '@/components/documentation/DocsSidebar'
import { DocsContent } from '@/components/documentation/DocsContent'
import { categories } from './_data'
import { OG_IMAGE_URL, siteUrl } from '@/lib/config/site'

export default function DocumentationPage() {
  const { slug } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { signIn } = useAuthActions()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleSignUp = () => {
    if (isElectron()) {
      navigate('/signup')
      return
    }
    signIn('google', { redirectTo: `${window.location.origin}/auth` })
  }

  const flatArticles = useMemo(
    () => categories.flatMap((c) => c.articles),
    []
  )

  const activeArticle = flatArticles.find((a) => a.slug === slug)

  // Scroll to hash anchor or top on slug change
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
        return
      }
    }
    window.scrollTo(0, 0)
  }, [slug, location.hash])

  // Close mobile drawer on navigation
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [slug])

  // Redirect to getting-started if no slug or invalid slug
  if (!slug || !activeArticle) {
    return <Navigate to="/documentation/getting-started" replace />
  }

  return (
    <div className="min-h-screen bg-background px-4 lg:px-0 overflow-x-hidden">
      <Helmet>
        <title>{activeArticle.title} - Documentation - Footage</title>
        <meta name="description" content={activeArticle.description} />
        <link rel="canonical" href={siteUrl(`/documentation/${activeArticle.slug}`)} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${activeArticle.title} - Footage Documentation`} />
        <meta property="og:description" content={activeArticle.description} />
        <meta property="og:url" content={siteUrl(`/documentation/${activeArticle.slug}`)} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:site_name" content="Footage" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${activeArticle.title} - Footage Documentation`} />
        <meta name="twitter:description" content={activeArticle.description} />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>

      <div className="max-w-7xl mx-auto border-x border-border">
        <Navbar isAuthenticated={isAuthenticated} onSignUp={handleSignUp} />
        <main className="flex flex-col divide-y divide-border pt-16">
          <SectionHeader>
            <div className="flex flex-col items-center justify-center gap-4 mt-4">
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-medium tracking-tighter text-center text-balance">
                Documentation
              </h1>
              <p className="text-muted-foreground md:text-lg text-center">
                Learn how to create videos with Footage
              </p>
            </div>
          </SectionHeader>

          <div className="flex min-h-[calc(100vh-20rem)]">
            <DocsSidebar
              categories={categories}
              mobileOpen={mobileMenuOpen}
              onMobileClose={() => setMobileMenuOpen(false)}
            />
            <div className="flex-1 min-w-0">
              {/* Mobile: sticky bar with menu button + current article title */}
              <div className="md:hidden sticky top-16 z-30 bg-background border-b border-border px-4 py-3 flex items-center gap-2">
                <button onClick={() => setMobileMenuOpen(true)}>
                  <Menu className="h-5 w-5" />
                </button>
                <span className="text-sm font-medium">{activeArticle.title}</span>
              </div>
              <DocsContent article={activeArticle} allArticles={flatArticles} />
            </div>
          </div>

          <HomepageFooter />
        </main>
      </div>
    </div>
  )
}
